import { NumberIncrementComposition } from '@codeleap/web'
import { textVariant } from './Text'
import { createStyles } from '@codeleap/styles'
import { StyleRegistry } from '../styles'

const createNumberIncrementVariant = createStyles<NumberIncrementComposition>

export const NumberIncrementStyles = {
  default: createNumberIncrementVariant(theme => {
    const height = theme.size.large
    return {
      wrapper: {
        ...theme.presets.column,
        width: 'auto',
      },
      label: {
        ...textVariant('p2'),
        color: theme.colors.textHeadingRegular,
        ...theme.spacing.marginBottom(1),
      },
      description: {
        ...textVariant('p4'),
        color: theme.colors.textCaption,
        ...theme.spacing.marginBottom(1),
      },
      errorMessage: {
        ...textVariant('p4'),
        color: theme.baseColors.redSolid400,
        ...theme.spacing.marginTop(1),
      },
      input: {
        ...textVariant('p1'),
        borderWidth: 0,
        textAlign: 'center',
        width: '100%',
        flex: 1,
      },
      'input:disabled': {
        color: theme.colors.fieldRegularLabelDisabled,
      },
      'input:error': {
        color: theme.colors.fieldNegativeLabelDefault,
      },
      placeholder: {
        color: theme.colors.fieldRegularLabelDefault,
      },
      'placeholder:disabled': {
        color: theme.colors.fieldRegularLabelDefault,
      },
      innerWrapper: {
        ...theme.spacing.paddingHorizontal(2),
        ...theme.spacing.paddingVertical(2),
        ...theme.presets.row,
        ...theme.presets.alignCenter,
        ...theme.presets.justifySpaceBetween,
        maxHeight: height,
        minHeight: height,
        borderRadius: theme.radius.regular,
        backgroundColor: theme.colors.fieldRegularBgDefault,
        ...theme.border({
          color: theme.colors.fieldRegularStrokeDefault,
          width: theme.stroke.regular,
        }),
      },
      'innerWrapper:cursor': {
        cursor: 'text',
      },
      'innerWrapper:focus': {
        ...theme.border({
          color: theme.colors.fieldRegularStrokeDefault,
          width: theme.stroke.regular,
        }),
      },
      'innerWrapper:error': {
        ...theme.border({
          color: theme.colors.fieldNegativeStrokeDefault,
          width: theme.stroke.regular,
        }),
      },
      'innerWrapper:disabled': {
        ...theme.border({
          color: theme.colors.fieldRegularStrokeDisabled,
          width: theme.stroke.regular,
        }),
        cursor: 'not-allowed',
      },
      iconIcon: {
        height: theme.values.iconSize[3],
        width: theme.values.iconSize[3],
        color: theme.colors.fieldRegularIconDefault,
      },
      'leftIconIcon:disabled': {
        color: theme.colors.fieldRegularIconDisabled,
        cursor: 'not-allowed',
      },
      'rightIconIcon:disabled': {
        color: theme.colors.fieldRegularIconDisabled,
        cursor: 'not-allowed',
      },
      iconTouchableWrapper: {
        ...theme.spacing.padding(0),
        height: theme.values.iconSize[3],
        width: theme.values.iconSize[3],
        borderWidth: 0,
        backgroundColor: 'transparent',
        ...theme.border({
          color: theme.colors.buttonHighlightPrimaryBgDefault,
          width: 0,
        }),
      },
      leftIconTouchableWrapper: {
        ...theme.spacing.marginRight(2),
      },
      'leftIconTouchableWrapper:disabled': {
        backgroundColor: 'transparent',
        ...theme.border({ color: 'transparent', width: 0 }),
      },
      rightIconTouchableWrapper: {
        ...theme.spacing.marginLeft(2),
      },
      'rightIconTouchableWrapper:disabled': {
        backgroundColor: 'transparent',
        ...theme.border({ color: 'transparent', width: 0 }),
      },
    }
  }),
  noError: createNumberIncrementVariant(theme => ({
    errorMessage: {
      display: 'none',
    },
  })),
  line: createNumberIncrementVariant(theme => ({
    innerWrapper: {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.neutralSolid500,
        width: theme.stroke.regular,
        directions: ['bottom'],
      }),
      borderRadius: 0,
    },
    'innerWrapper:focus': {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.primarySolid300,
        width: theme.stroke.regular,
        directions: ['bottom'],
      }),
    },
    'innerWrapper:error': {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.redSolid400,
        width: theme.stroke.regular,
        directions: ['bottom'],
      }),
    },
    'innerWrapper:disabled': {
      ...theme.border({ color: theme.baseColors.neutralSolid500, width: 0 }),
      ...theme.border({
        color: theme.baseColors.neutralSolid200,
        width: theme.stroke.regular,
        directions: ['bottom'],
      }),
    },
  })),
  pill: createNumberIncrementVariant(theme => ({
    innerWrapper: {
      borderRadius: theme.radius.infinite,
    },
  })),
  normal: createNumberIncrementVariant(theme => ({
    innerWrapper: {
      borderRadius: theme.radius.regular,
    },
  })),
}

StyleRegistry.registerVariants('NumberIncrement', NumberIncrementStyles)
